import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { HealthCheck, Tag, UserRoleEnum } from '../../../generated/graphql';
import ShowOnRole from '../ShowOnRole/ShowOnRole';
import { newGuid } from '../../../Utils';
import { ChevronDown, ChevronUp, X, Info, ZoomIn, Upload, Loader2 } from 'lucide-react';
import TagBadge from '../TagBadge/TagBadge';

export type EssentialRow = {
    title: string;
    value: ReactNode;
    link?: string;
    className?: string;
};

type EssentialsNewProps = {
    isAdmin: boolean;
    style?: CSSProperties;
    children?: ReactNode;
    className?: string;
    tags?: Tag[];
    healthChecks?: HealthCheck[];
    preview?: string;
    previewThumbnails?: { url: string; name: string }[];
    rows: EssentialRow[];
    onRemoveTag?: (tagId: string) => void;
    onRemoveHealthCheck?: (healthCheckId: string) => void;
    onEditPreview?: () => void;
    previewUploading?: boolean;
    /** Stable context name used to remember whether this panel is expanded. */
    persistenceKey?: string;
};

const TAG_PREVIEW_LIMIT = 6;
const HEALTH_CHECK_PREVIEW_LIMIT = 8;
const ESSENTIALS_STORAGE_PREFIX = 'cms:essentials:expanded:v1';

const readExpandedPreference = (storageKey: string): boolean => {
    if (typeof window === 'undefined') return true;

    try {
        const storedValue = window.localStorage.getItem(storageKey);
        return storedValue === 'false' ? false : true;
    } catch {
        return true;
    }
};

const EssentialsNew = (props: EssentialsNewProps) => {
    const { preview, previewThumbnails, tags, healthChecks, rows, style, className, isAdmin, onRemoveTag, onRemoveHealthCheck, onEditPreview, previewUploading, persistenceKey = 'default' } = props;
    const expandedStorageKey = `${ESSENTIALS_STORAGE_PREFIX}:${persistenceKey}`;
    const loadedStorageKeyRef = useRef(expandedStorageKey);
    const [expanded, setExpanded] = useState(() => readExpandedPreference(expandedStorageKey));
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const [showAllTags, setShowAllTags] = useState(false);
    const [showAllHealthChecks, setShowAllHealthChecks] = useState(false);
    const availableTags = (tags ?? []).filter(Boolean);
    const availableHealthChecks = (healthChecks ?? []).filter(Boolean);
    const visibleTags = showAllTags ? availableTags : availableTags.slice(0, TAG_PREVIEW_LIMIT);
    const visibleHealthChecks = showAllHealthChecks
        ? availableHealthChecks
        : availableHealthChecks.slice(0, HEALTH_CHECK_PREVIEW_LIMIT);

    useEffect(() => {
        if (loadedStorageKeyRef.current !== expandedStorageKey) {
            loadedStorageKeyRef.current = expandedStorageKey;
            setExpanded(readExpandedPreference(expandedStorageKey));
        }
    }, [expandedStorageKey]);

    const toggleExpanded = () => {
        setExpanded(currentValue => {
            const nextValue = !currentValue;
            try {
                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(expandedStorageKey, String(nextValue));
                }
            } catch {
                // Storage may be unavailable; the control still works for this session.
            }
            return nextValue;
        });
    };

    return (
        <div className={`card overflow-hidden ${className || ''}`} style={style}>
            <button
                type="button"
                onClick={toggleExpanded}
                aria-expanded={expanded}
                className="w-full flex items-center justify-between px-4 py-3"
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    outline: 'none',
                    borderBottom: expanded ? '1px solid var(--border-default)' : 'none',
                }}
            >
                <div className="flex items-center gap-2">
                    <Info size={16} style={{ color: 'var(--text-tertiary)' }} />
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Essentials
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="hidden sm:inline text-xs font-normal" style={{ color: 'var(--text-tertiary)' }}>
                        {rows.length} {rows.length === 1 ? 'detail' : 'details'}
                        {availableTags.length > 0 && ` · ${availableTags.length} ${availableTags.length === 1 ? 'tag' : 'tags'}`}
                        {availableHealthChecks.length > 0 && ` · ${availableHealthChecks.length} checks`}
                    </span>
                    {expanded ? <ChevronUp size={16} style={{ color: 'var(--text-tertiary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-tertiary)' }} />}
                </div>
            </button>

            {expanded && (
                <div className="px-4 py-3 animate-fade-in">
                    <div className="flex gap-6">
                        <div className="flex-1 min-w-0">
                            {/* Properties use the available width before wrapping to another row. */}
                            <dl className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-3">
                                {rows.map((r, i) => (
                                    <div key={i} className={`min-w-0 ${r.className || ''}`}>
                                        <dt className="text-[11px] font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>
                                            {r.title}
                                        </dt>
                                        <dd className="text-sm break-words" style={{ color: 'var(--text-primary)', margin: 0 }}>
                                            {r.link ? (
                                                <Link to={r.link} className="hover:underline" style={{ color: 'var(--accent-text)' }}>
                                                    {r.value}
                                                </Link>
                                            ) : r.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>

                            {(availableTags.length > 0 || availableHealthChecks.length > 0) && (
                                <div className="flex flex-col gap-2.5 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                    {availableTags.length > 0 && (
                                        <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                                            <span className="text-xs font-medium sm:w-24 sm:pt-0.5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                                                Tags
                                            </span>
                                            <div className="flex flex-wrap gap-1.5 min-w-0">
                                                {visibleTags.map(t => (
                                                    <TagBadge key={t.id || newGuid()} tag={t} className="inline-flex items-center gap-1">
                                                        <span className="font-semibold">{t.type}:</span>
                                                        <span>{t.name}</span>
                                                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); onRemoveTag?.(t.id || ''); }}
                                                                className="ml-0.5 rounded-full hover:opacity-70"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                                                                aria-label={`Remove ${t.name} tag`}
                                                            >
                                                                <X size={10} />
                                                            </button>
                                                        </ShowOnRole>
                                                    </TagBadge>
                                                ))}
                                                {availableTags.length > TAG_PREVIEW_LIMIT && (
                                                    <button
                                                        type="button"
                                                        className="badge-default"
                                                        aria-expanded={showAllTags}
                                                        onClick={() => setShowAllTags(value => !value)}
                                                    >
                                                        {showAllTags ? 'Show less' : `+${availableTags.length - TAG_PREVIEW_LIMIT} more`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {availableHealthChecks.length > 0 && (
                                        <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                                            <span className="text-xs font-medium sm:w-24 sm:pt-0.5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                                                Health Checks
                                            </span>
                                            <div className="flex flex-wrap gap-1.5 min-w-0">
                                                {visibleHealthChecks.map(t => (
                                                    <span key={t.id || newGuid()} className="badge-success inline-flex items-center gap-1">
                                                        <span>{t.name}</span>
                                                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); onRemoveHealthCheck?.(t.id || ''); }}
                                                                className="ml-0.5 rounded-full hover:opacity-70"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                                                                aria-label={`Remove ${t.name} health check`}
                                                            >
                                                                <X size={10} />
                                                            </button>
                                                        </ShowOnRole>
                                                    </span>
                                                ))}
                                                {availableHealthChecks.length > HEALTH_CHECK_PREVIEW_LIMIT && (
                                                    <button
                                                        type="button"
                                                        className="badge-default"
                                                        aria-expanded={showAllHealthChecks}
                                                        onClick={() => setShowAllHealthChecks(value => !value)}
                                                    >
                                                        {showAllHealthChecks ? 'Show less' : `+${availableHealthChecks.length - HEALTH_CHECK_PREVIEW_LIMIT} more`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Preview Image + Type Thumbnails */}
                        {(preview || onEditPreview || (previewThumbnails && previewThumbnails.length > 0)) && (
                            <div className="flex-shrink-0 flex flex-col items-end gap-2">
                                {preview && (
                                    <div
                                        className="rounded-xl overflow-hidden border group cursor-pointer"
                                        style={{ position: 'relative', display: 'block', borderColor: 'var(--border-default)', width: 160, height: 160, minWidth: 160, flexShrink: 0 }}
                                        onClick={() => setLightboxUrl(preview)}
                                    >
                                        <img src={preview} alt="Preview" className="w-full h-full object-contain" />
                                        <div
                                            className="absolute inset-0 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{ backgroundColor: 'rgba(0,0,0,0.3)', pointerEvents: 'none', zIndex: 1 }}
                                        >
                                            <ZoomIn size={24} style={{ color: 'white' }} />
                                        </div>
                                        {onEditPreview && (
                                            <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                                <button
                                                    type="button"
                                                    className="rounded-lg"
                                                    style={{ position: 'absolute', top: 8, right: 8, left: 'auto', bottom: 'auto', zIndex: 2, width: 32, height: 32, margin: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', border: 'none', cursor: previewUploading ? 'wait' : 'pointer', boxSizing: 'border-box' }}
                                                    disabled={previewUploading}
                                                    onClick={(e) => { e.stopPropagation(); onEditPreview(); }}
                                                    title="Replace thumbnail"
                                                    aria-label="Replace thumbnail"
                                                >
                                                    {previewUploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                                                </button>
                                            </ShowOnRole>
                                        )}
                                    </div>
                                )}
                                {!preview && onEditPreview && (
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                        <button
                                            type="button"
                                            className="h-40 w-40 rounded-xl border flex flex-col items-center justify-center gap-2 text-sm"
                                            style={{ borderColor: 'var(--border-default)', borderStyle: 'dashed', backgroundColor: 'var(--bg-subtle)', color: 'var(--text-tertiary)', cursor: previewUploading ? 'wait' : 'pointer' }}
                                            disabled={previewUploading}
                                            onClick={onEditPreview}
                                        >
                                            {previewUploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                                            <span>{previewUploading ? 'Uploading…' : 'Add thumbnail'}</span>
                                        </button>
                                    </ShowOnRole>
                                )}
                                {previewThumbnails && previewThumbnails.length > 0 && (
                                    <div className="flex gap-1.5 flex-wrap justify-end">
                                        {previewThumbnails.map((t, i) => (
                                            <div
                                                key={i}
                                                className="rounded-lg overflow-hidden border relative group cursor-pointer"
                                                style={{ borderColor: 'var(--border-default)', width: 48, height: 48 }}
                                                title={t.name}
                                                onClick={() => setLightboxUrl(t.url)}
                                            >
                                                <img src={t.url} alt={t.name} className="w-full h-full object-cover" />
                                                <div
                                                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                    style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                                                >
                                                    <ZoomIn size={12} style={{ color: 'white' }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Extra content */}
                    {props.children}
                </div>
            )}

            {/* Lightbox overlay — portaled to body to escape dialog transform containment */}
            {lightboxUrl && createPortal(
                <div
                    className="fixed inset-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999 }}
                    onClick={() => setLightboxUrl(null)}
                >
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={lightboxUrl}
                            alt="Full size"
                            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }}
                        />
                        <button
                            onClick={() => setLightboxUrl(null)}
                            className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center border-none"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', cursor: 'pointer' }}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default EssentialsNew;
